/**
 * Reorders items in a tree list structure based on drag and drop operations
 * @param {Object} e - Event object containing drag information
 * @param {Array} actions - Array of action items to reorder
 * @returns {Array} - Reordered array of actions
 */
export function reorderTreeList(e, actions) {
    // Extract drag source and destination information from the event
    const { draggedOver, dragged } = e;
    
    // Initialize variables to track drag operation state
    let draggedOverElement,    // The element being dragged over (destination)
        draggedElement,        // The element being dragged (source)
        isBetween = false,     // Whether the drop position is between items
        shouldAllowDrop = true; // Whether the drop operation should be allowed

    // Create a copy of the actions array to avoid mutating the original
    const data = actions.slice();

    // Determine the destination element based on drag coordinates
    if (draggedOver.length === 1) {
        // Dragging to a top-level item
        draggedOverElement = data[draggedOver[0]];
    } else {
        // Dragging to a nested item (child of another item)
        draggedOverElement = data[draggedOver[0]].actions[draggedOver[1]];
    }

    // Determine the source element being dragged
    if (dragged.length === 1) {
        // Dragging a top-level item
        draggedElement = data[dragged[0]];
    } else {
        // Dragging a nested item
        draggedElement = data[dragged[0]].actions[dragged[1]];
    }

    // Check if we're dragging over a "Button Group" to determine drop position
    if (draggedOverElement.actionDisplay === "Button Group") {
        // Calculate if the drop position is between items or on the item itself
        let row = e.nativeEvent.target;
        let coord = row.getBoundingClientRect();
        let { clientY } = e.nativeEvent;
        let coordElementMiddle = coord.top + coord.height / 2;
        let coordMiddleDiff = coordElementMiddle - clientY;
        
        // If drop position is within 5px of the middle, consider it "between" items
        isBetween = coordMiddleDiff < 5 && coordMiddleDiff > -5;
    }

    // Prevent dropping Button Groups into other Button Groups or between items
    if (
        draggedElement.actionDisplay === "Button Group" &&
        (isBetween || draggedOver.length === 2)
    ) {
        shouldAllowDrop = false;
    }

    // Initialize actions array for Button Group if it doesn't exist
    if (draggedOverElement.actionDisplay === "Button Group" && draggedOverElement.actions === undefined) {
        draggedOverElement.actions = [];
    }

    // Handle the actual reordering based on drop position and element types
    if (isBetween) {
        // Dropping between items - add to the destination's actions array
        if (dragged.length === 1) {
            // Moving a top-level item into a nested position
            data[draggedOver[0]].actions.push(draggedElement);
            data.splice(dragged[0], 1);
        } else if (dragged.length === 2) {
            // Moving a nested item to another nested position
            data[dragged[0]].actions.splice(dragged[1], 1);
            data[draggedOver[0]].actions.push(draggedElement);
        }
    } else if (dragged[0] > draggedOver[0]) {
        // Dragging from a higher index to a lower index (moving up in the list)
        if (dragged.length === 2 && draggedOver.length === 1) {
            // Moving a nested item to a top-level position
            data[dragged[0]].actions.splice(dragged[1], 1);
            data.splice(draggedOver[0], 0, draggedElement);
        } else if (draggedOver.length === 2 && dragged.length === 1) {
            // Moving a top-level item to a nested position
            data[draggedOver[0]].actions.splice(draggedOver[1], 0, draggedElement);
            data.splice(dragged[0], 1);
        } else if (draggedOver.length === 2 && dragged.length === 2) {
            // Moving a nested item to another nested position
            data[draggedOver[0]].actions.splice(draggedOver[1], 0, draggedElement);
            data[dragged[0]].actions.splice(dragged[1], 1);
        } else if (draggedOver.length === 1 && dragged.length === 1) {
            // Moving a top-level item to another top-level position
            data.splice(dragged[0], 1);
            data.splice(draggedOver[0], 0, draggedElement);
        } 
    } else if (dragged[0] === draggedOver[0] && dragged.length === 2 && draggedOver.length === 1) {
        // Moving a nested item to the top level within the same parent
        data[draggedOver[0]].actions.splice(dragged[1], 1);
        data.splice(draggedOver[0], 0, draggedElement);
    } else {
        // Dragging from a lower index to a higher index (moving down in the list)
        if (dragged.length === 2 && draggedOver.length === 1) {
            // Moving a nested item to a top-level position
            data[dragged[0]].actions.splice(dragged[1], 1);
            data.splice(draggedOver[0] + 1, 0, draggedElement);
        } else if (draggedOver.length === 2 && dragged.length === 1) {
            // Moving a top-level item to a nested position
            data[draggedOver[0]].actions.splice(draggedOver[1] + 1, 0, draggedElement);
            data.splice(dragged[0], 1);
        } else if (draggedOver.length === 2 && dragged.length === 2) {
            // Moving a nested item to another nested position
            data[dragged[0]].actions.splice(dragged[1], 1);
            data[draggedOver[0]].actions.splice(draggedOver[1], 0, draggedElement);
        } else if (draggedOver.length === 1 && dragged.length === 1) {
            // Moving a top-level item to another top-level position
            data.splice(dragged[0], 1);
            data.splice(draggedOver[0], 0, draggedElement);
        }
    }

    return data;
}